import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  UploadCloud, Edit, Trash2, CheckCircle, 
  XCircle, ChevronLeft, ChevronRight, Search, 
  Filter 
} from 'lucide-react';
import './AdminCategory.css';

const API_URL = 'https://api.sohanihandicrafts.com:5010'; 

const AdminCategory = () => {
    // Data State
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Form State
    const [editingId, setEditingId] = useState(null);
    const [categoryName, setCategoryName] = useState('');
    // Removed selectedMainCategory state
    const [categoryImage, setCategoryImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const fileInputRef = useRef(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(8);
    const [totalCategories, setTotalCategories] = useState(0);

    // --- FETCH DATA ---
    // Removed fetchMainCategories function

    const fetchCategories = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/category/categories`, {
                params: { page: currentPage, limit: itemsPerPage }
            });
            setCategories(response.data.categories || []);
            setTotalCategories(response.data.totalCategories || 0);
        } catch (err) {
            Swal.fire('Error', 'Could not load categories', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, [currentPage]);

    // --- HANDLERS ---
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCategoryImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setCategoryName('');
        // Removed setSelectedMainCategory
        setCategoryImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!categoryName.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Category name is required.' });
            return;
        }
        if (!editingId && !categoryImage) {
            Swal.fire({ icon: 'warning', title: 'Image Required', text: 'Please upload an image.' });
            return;
        }

        const formData = new FormData();
        formData.append('name', categoryName);
        // Removed appending mainCategory
        if (categoryImage) formData.append('categoryImage', categoryImage);

        const url = editingId 
            ? `${API_URL}/api/category/update/${editingId}` 
            : `${API_URL}/api/category/add`;
        const method = editingId ? 'put' : 'post';

        try {
            await axios({ method, url, data: formData });
            Swal.fire({ 
                icon: 'success', 
                title: editingId ? 'Updated!' : 'Created!', 
                showConfirmButton: false, 
                timer: 1500 
            });
            resetForm();
            fetchCategories();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.msg || 'Server Error', 'error');
        }
    };

    const handleEdit = (cat) => {
        setEditingId(cat._id);
        setCategoryName(cat.name);
        setImagePreview(`${API_URL}/${cat.image}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete Category?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${API_URL}/api/category/delete/${id}`);
                    fetchCategories();
                    Swal.fire('Deleted!', 'Category has been removed.', 'success');
                } catch (err) {
                    Swal.fire('Error', 'Failed to delete.', 'error');
                }
            }
        });
    };

    const handleToggleStatus = async (id) => {
        try {
            await axios.patch(`${API_URL}/api/category/toggle/${id}`);
            fetchCategories();
            const Toast = Swal.mixin({
                toast: true, position: 'top-end', showConfirmButton: false, timer: 2000
            });
            Toast.fire({ icon: 'success', title: 'Status Updated' });
        } catch (err) {
            console.error(err);
        }
    };

    const totalPages = Math.ceil(totalCategories / itemsPerPage);

    return (
        <div className="adm-cat-wrapper">
            
            <div className="adm-cat-header">
                <div>
                    <h1>Category Manager</h1>
                    <p>Manage your product categories</p>
                </div>
                <div className="adm-header-stats">
                    <span>Total Categories: <strong>{totalCategories}</strong></span>
                </div>
            </div>

            <div className="adm-cat-layout">
                
                {/* --- LEFT: FORM SECTION --- */}
                <div className="adm-cat-form-card">
                    <div className="adm-card-header">
                        <h3>{editingId ? 'Edit Category' : 'Create New Category'}</h3>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        {/* Removed Main Category Select Box */}

                        <div className="adm-form-group">
                            <label>Category Name</label>
                            <input 
                                type="text" 
                                placeholder="e.g. Living Room Furniture" 
                                value={categoryName}
                                onChange={(e) => setCategoryName(e.target.value)}
                            />
                        </div>

                        <div className="adm-form-group">
                            <label>Category Image</label>
                            <div 
                                className={`adm-file-drop ${imagePreview ? 'has-image' : ''}`}
                                onClick={() => fileInputRef.current.click()}
                            >
                                <input 
                                    type="file" 
                                    hidden 
                                    ref={fileInputRef} 
                                    onChange={handleFileChange} 
                                    accept="image/*"
                                />
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Preview" className="adm-img-preview" />
                                ) : (
                                    <div className="adm-drop-placeholder">
                                        <UploadCloud size={32} />
                                        <span>Click to Upload</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="adm-form-actions">
                            {editingId && (
                                <button type="button" className="adm-btn-cancel" onClick={resetForm}>
                                    Cancel
                                </button>
                            )}
                            <button type="submit" className="adm-btn-submit">
                                {editingId ? 'Update Category' : 'Save Category'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* --- RIGHT: TABLE SECTION --- */}
                <div className="adm-cat-table-card">
                    <div className="adm-table-tools">
                        <div className="adm-search-box">
                            <Search size={18} />
                            <input type="text" placeholder="Search categories..." />
                        </div>
                        <button className="adm-filter-btn"><Filter size={18}/></button>
                    </div>

                    <div className="adm-table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Category Name</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="4" className="text-center">Loading...</td></tr>
                                ) : categories.map((cat) => (
                                    <tr key={cat._id}>
                                        <td>
                                            <div className="adm-tbl-img">
                                                <img 
                                                    src={`${API_URL}/${cat.image}`} 
                                                    onError={(e) => e.target.src = 'https://via.placeholder.com/50'} 
                                                    alt="cat" 
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <span className="adm-tbl-name big">{cat.name}</span>
                                        </td>
                                        <td>
                                            <button 
                                                className={`adm-status-badge ${cat.status ? 'active' : 'inactive'}`}
                                                onClick={() => handleToggleStatus(cat._id)}
                                            >
                                                {cat.status ? 'Active' : 'Disabled'}
                                            </button>
                                        </td>
                                        <td>
                                            <div className="adm-action-btns">
                                                <button className="btn-icon edit" onClick={() => handleEdit(cat)}>
                                                    <Edit size={18} />
                                                </button>
                                                <button className="btn-icon del" onClick={() => handleDelete(cat._id)}>
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {categories.length === 0 && !isLoading && (
                                    <tr><td colSpan="4" className="text-center">No categories found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="adm-pagination">
                        <button 
                            disabled={currentPage === 1} 
                            onClick={() => setCurrentPage(p => p - 1)}
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <span>Page {currentPage} of {totalPages || 1}</span>
                        <button 
                            disabled={currentPage >= totalPages} 
                            onClick={() => setCurrentPage(p => p + 1)}
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminCategory;